import * as THREE from "three"

export const createViewerModesController = ({
  state,
  element,
  stage,
  toolbar,
  exportPanel,
  referenceLayerPanel,
  labelsPanel,
  renderer,
  scene,
  camera,
  perspectiveCamera,
  orthographicCamera,
  controls,
  setActiveCamera,
  applyOrbitBounds,
  refreshModelFraming,
  defaultViewAnchor,
  default2DFocusPoint,
  walkthroughFocusPoint,
  walkthroughPoseFromSettings,
  deriveWalkthroughHelperSettings,
  cloneViewerSettings,
  syncCablingPlanOverlayPresentation,
  syncNavigationPointVisibility,
  syncCablingPlanForm,
  notifyEmbedStateChanged,
  setFeedback,
  slugify,
  parseFloatOr,
  clampNumber,
  is2DLikeMode
}) => {
  const setLabelsMenuOpen = (open) => {
    state.labelsMenuOpen = Boolean(open)
    if (labelsPanel) labelsPanel.hidden = !state.labelsMenuOpen
    const trigger = toolbar?.querySelector('[data-viewer-action="labels"]')
    if (trigger) trigger.setAttribute("aria-expanded", state.labelsMenuOpen ? "true" : "false")
  }

  const setReferenceLayerMenuOpen = (open) => {
    state.referenceLayerMenuOpen = Boolean(open)
    if (referenceLayerPanel) referenceLayerPanel.hidden = !state.referenceLayerMenuOpen
    const trigger = toolbar?.querySelector('[data-viewer-action="reference-layers"]')
    if (trigger) trigger.setAttribute("aria-expanded", state.referenceLayerMenuOpen ? "true" : "false")
  }

  const setExportMenuOpen = (open) => {
    state.exportMenuOpen = Boolean(open)
    if (exportPanel) exportPanel.hidden = !state.exportMenuOpen
    const trigger = toolbar?.querySelector('[data-viewer-action="export"]')
    if (trigger) trigger.setAttribute("aria-expanded", state.exportMenuOpen ? "true" : "false")
  }

  const setActiveButton = (action) => {
    toolbar?.querySelectorAll("[data-viewer-action]").forEach((button) => {
      const isToggle = ["view-3d", "view-2d", "reference-layers", "walkthrough", "mapping-mode", "viewer-settings", "auto-rotate", "orbit-lock", "labels", "export"].includes(button.dataset.viewerAction)
      if (!isToggle) return

      const disabled = (
        (button.dataset.viewerAction === "walkthrough" && !state.model) ||
        (button.dataset.viewerAction === "auto-rotate" && is2DLikeMode(state.viewMode)) ||
        (button.dataset.viewerAction === "orbit-lock" && is2DLikeMode(state.viewMode)) ||
        (button.dataset.viewerAction === "mapping-mode" && !state.capabilities.mapping) ||
        (button.dataset.viewerAction === "viewer-settings" && !state.capabilities.viewerSettings) ||
        (button.dataset.viewerAction === "export" && !state.capabilities.exportImage)
      )
      let active = false
      if (button.dataset.viewerAction === "auto-rotate") active = state.autoRotate
      if (button.dataset.viewerAction === "orbit-lock") active = state.orbitLock
      if (button.dataset.viewerAction === "labels") active = state.labelsVisible || state.labelsMenuOpen
      if (button.dataset.viewerAction === "reference-layers") active = state.visibleReferenceLayerIds.size > 0 || state.referenceLayerMenuOpen
      if (button.dataset.viewerAction === "export") active = state.exportMenuOpen
      if (button.dataset.viewerAction === "view-3d") active = state.viewMode === "3d"
      if (button.dataset.viewerAction === "view-2d") active = state.viewMode === "2d"
      if (button.dataset.viewerAction === "walkthrough") active = state.viewMode === "walkthrough"
      if (button.dataset.viewerAction === "mapping-mode") active = state.sidePanelMode === "mapping"
      if (button.dataset.viewerAction === "viewer-settings") active = state.sidePanelMode === "viewer-settings"
      if (action && button.dataset.viewerAction === action && action === "auto-rotate") active = state.autoRotate

      button.disabled = disabled
      button.classList.toggle("is-disabled", disabled)
      button.setAttribute("aria-disabled", disabled ? "true" : "false")
      button.classList.toggle("is-active", active)
      button.setAttribute("aria-pressed", active ? "true" : "false")
    })
  }

  const prepareForViewChange = (mode) => {
    state.viewMode = mode
    state.autoRotate = false
    controls.autoRotate = false
    applyOrbitBounds({ mode, update: false })
  }

  const configureOrthographicCamera = () => {
    if (!state.framing) return

    const width = element.clientWidth || 1
    const height = element.clientHeight || 1
    const aspect = width / height
    const sizeX = Math.max(state.framing.size.x, 1)
    const sizeZ = Math.max(state.framing.size.z, 1)
    const fitSpan = Math.max(sizeZ, sizeX / Math.max(aspect, 0.0001), state.framing.sphere?.radius || 1) * 0.58

    if (aspect >= 1) {
      orthographicCamera.left = -fitSpan * aspect
      orthographicCamera.right = fitSpan * aspect
      orthographicCamera.top = fitSpan
      orthographicCamera.bottom = -fitSpan
    } else {
      orthographicCamera.left = -fitSpan
      orthographicCamera.right = fitSpan
      orthographicCamera.top = fitSpan / aspect
      orthographicCamera.bottom = -fitSpan / aspect
    }

    orthographicCamera.near = 0.01
    orthographicCamera.far = Math.max((state.framing.size.y || 1) * 12, (state.framing.sphere?.radius || 1) * 12, 100)
    orthographicCamera.updateProjectionMatrix()
  }

  const applyDefault3DCamera = () => {
    if (!state.framing) return

    const { distance } = state.framing
    const anchor = defaultViewAnchor()
    if (!anchor) return
    const yaw = THREE.MathUtils.degToRad(state.viewerSettings.defaultYawDegrees)
    const pitch = THREE.MathUtils.degToRad(clampNumber(state.viewerSettings.defaultPitchDegrees, -80, 80))
    const zoomDistance = Math.max(distance * state.viewerSettings.defaultZoomMultiplier, 0.1)
    const horizontalDistance = Math.cos(pitch) * zoomDistance

    camera.position.set(
      anchor.x + Math.cos(yaw) * horizontalDistance,
      anchor.y + Math.sin(pitch) * zoomDistance,
      anchor.z + Math.sin(yaw) * horizontalDistance
    )
    controls.target.copy(anchor)
    const previousDamping = controls.enableDamping
    controls.enableDamping = false
    controls.update()
    controls.enableDamping = previousDamping
  }

  const apply3DView = () => {
    prepareForViewChange("3d")
    setActiveCamera(perspectiveCamera)
    refreshModelFraming()
    if (!state.framing) return
    applyDefault3DCamera()
    applyOrbitBounds()
    syncCablingPlanOverlayPresentation()
    syncNavigationPointVisibility()
    setActiveButton()
    syncCablingPlanForm()
    notifyEmbedStateChanged()
  }

  const apply2DView = () => {
    refreshModelFraming()
    if (!state.framing) return
    const focusPoint = default2DFocusPoint()
    if (!focusPoint) return

    setActiveCamera(orthographicCamera)
    prepareForViewChange("2d")
    configureOrthographicCamera()

    const orthoDistance = Math.max(state.framing.size.y, state.framing.sphere?.radius || 1, 10)
    orthographicCamera.position.set(focusPoint.x, focusPoint.y + orthoDistance, focusPoint.z)
    controls.target.copy(focusPoint)
    orthographicCamera.zoom = state.ortho2DZoom || Math.max(Math.abs(parseFloatOr(state.viewerSettings.default2DZoomMultiplier, 1)), 0.0001)
    orthographicCamera.lookAt(focusPoint)
    orthographicCamera.updateMatrixWorld(true)
    orthographicCamera.updateProjectionMatrix()

    const previousDamping = controls.enableDamping
    controls.enableDamping = false
    controls.update()
    controls.enableDamping = previousDamping
    applyOrbitBounds({ mode: "2d" })
    syncCablingPlanOverlayPresentation()
    syncNavigationPointVisibility()
    setActiveButton()
    syncCablingPlanForm()
    notifyEmbedStateChanged()
  }

  const applyOrthographic2DView = () => apply2DView()

  const applyWalkthroughView = () => {
    if (!state.model) {
      apply2DView()
      return
    }
    setActiveCamera(perspectiveCamera)
    refreshModelFraming()
    if (!state.framing) return
    prepareForViewChange("walkthrough")
    const exactPose = walkthroughPoseFromSettings()

    if (exactPose) {
      const syncedViewerSettings = deriveWalkthroughHelperSettings({
        baseSettings: state.viewerSettings,
        cameraPosition: exactPose.cameraPosition,
        targetPosition: exactPose.targetPosition
      })
      const syncedSavedViewerSettings = deriveWalkthroughHelperSettings({
        baseSettings: state.savedViewerSettings,
        cameraPosition: exactPose.cameraPosition,
        targetPosition: exactPose.targetPosition
      })
      if (syncedViewerSettings) state.viewerSettings = cloneViewerSettings(syncedViewerSettings)
      if (syncedSavedViewerSettings) state.savedViewerSettings = cloneViewerSettings(syncedSavedViewerSettings)

      const depthOffset = Math.max(
        Math.hypot(
          exactPose.cameraPosition.x - exactPose.targetPosition.x,
          exactPose.cameraPosition.z - exactPose.targetPosition.z
        ),
        0.1
      )
      camera.position.copy(exactPose.cameraPosition)
      controls.target.copy(exactPose.targetPosition)
      camera.near = Math.max(depthOffset / 100, 0.1)
      camera.far = Math.max(depthOffset * 200, 200)
    } else {
      const { box } = state.framing
      const focusPoint = walkthroughFocusPoint()
      if (!focusPoint) return
      const floorY = box.min.y
      const yaw = THREE.MathUtils.degToRad(state.viewerSettings.walkthroughYawDegrees)
      const pitch = THREE.MathUtils.degToRad(clampNumber(state.viewerSettings.walkthroughPitchDegrees, -80, 80))
      const depthOffset = Math.max(state.viewerSettings.walkthroughZoomMultiplier, 0.1)
      const cameraY = floorY + Math.max(state.viewerSettings.walkthroughEyeHeightRatio, 0)
      const targetY = cameraY + Math.tan(pitch) * depthOffset

      camera.position.set(
        focusPoint.x + Math.cos(yaw) * depthOffset,
        cameraY,
        focusPoint.z + Math.sin(yaw) * depthOffset
      )
      controls.target.set(focusPoint.x, targetY, focusPoint.z)
      camera.near = Math.max(depthOffset / 100, 0.1)
      camera.far = Math.max(depthOffset * 200, 200)
    }

    camera.updateProjectionMatrix()
    const previousDamping = controls.enableDamping
    controls.enableDamping = false
    controls.update()
    controls.enableDamping = previousDamping
    applyOrbitBounds()
    syncCablingPlanOverlayPresentation()
    syncNavigationPointVisibility()
    setActiveButton()
    syncCablingPlanForm()
    notifyEmbedStateChanged()
  }

  const applyDefaultView = () => {
    if (state.viewerSettings.defaultViewMode === "2d") {
      apply2DView()
      return
    }

    if (state.viewerSettings.defaultViewMode === "walkthrough") {
      applyWalkthroughView()
      return
    }

    apply3DView()
  }

  let queuedViewMode = null
  let queuedViewModeFrame = null

  const applyQueuedViewMode = () => {
    queuedViewModeFrame = null
    const nextMode = queuedViewMode
    queuedViewMode = null
    if (!nextMode) return

    if (nextMode === "2d") {
      apply2DView()
      return
    }

    if (nextMode === "walkthrough") {
      applyWalkthroughView()
      return
    }

    apply3DView()
  }

  const queueViewMode = (mode) => {
    queuedViewMode = mode === "2d-ortho" ? "2d" : mode
    if (queuedViewModeFrame) return
    queuedViewModeFrame = window.requestAnimationFrame(() => {
      applyQueuedViewMode()
    })
  }

  const resetView = () => {
    state.autoRotate = false
    controls.autoRotate = false
    applyDefaultView()
  }

  const fitCurrentView = () => {
    state.autoRotate = false
    controls.autoRotate = false

    if (state.viewMode === "2d") {
      state.ortho2DZoom = null
      apply2DView()
      return
    }

    if (state.viewMode === "walkthrough") {
      applyWalkthroughView()
      return
    }

    apply3DView()
  }

  const toggleAutoRotate = () => {
    state.autoRotate = !state.autoRotate
    if (state.autoRotate && is2DLikeMode(state.viewMode)) {
      applyDefaultView()
      state.autoRotate = true
    }
    controls.autoRotate = state.autoRotate
    controls.autoRotateSpeed = 1.35
    setActiveButton("auto-rotate")
  }

  const toggleFullscreen = async () => {
    if (!stage) return
    if (document.fullscreenElement === stage) {
      await document.exitFullscreen()
    } else {
      await stage.requestFullscreen()
    }
  }

  const exportCurrentView = (format) => {
    const normalizedFormat = format === "jpg" ? "jpg" : "png"
    renderer.render(scene, camera)

    let dataUrl = null
    if (normalizedFormat === "jpg") {
      const exportCanvas = document.createElement("canvas")
      exportCanvas.width = renderer.domElement.width
      exportCanvas.height = renderer.domElement.height
      const context = exportCanvas.getContext("2d")
      context.fillStyle = "#eef4fb"
      context.fillRect(0, 0, exportCanvas.width, exportCanvas.height)
      context.drawImage(renderer.domElement, 0, 0)
      dataUrl = exportCanvas.toDataURL("image/jpeg", 0.92)
    } else {
      dataUrl = renderer.domElement.toDataURL("image/png")
    }

    const downloadLink = document.createElement("a")
    downloadLink.href = dataUrl
    downloadLink.download = `${slugify(element.dataset.planName)}-${state.viewMode}.${normalizedFormat}`
    downloadLink.click()
    setFeedback(`Exported ${normalizedFormat.toUpperCase()} image.`, "success")
  }

  return {
    setLabelsMenuOpen,
    setReferenceLayerMenuOpen,
    setExportMenuOpen,
    setActiveButton,
    prepareForViewChange,
    configureOrthographicCamera,
    applyDefault3DCamera,
    apply3DView,
    apply2DView,
    applyOrthographic2DView,
    applyWalkthroughView,
    applyDefaultView,
    applyQueuedViewMode,
    queueViewMode,
    resetView,
    fitCurrentView,
    toggleAutoRotate,
    toggleFullscreen,
    exportCurrentView
  }
}
